import React from 'react';
import { FONTS } from '../../styles/tokens';
import type { AuditLogRow } from '../../lib/notifications-api';

interface Colors { text: string; textSecondary: string; card: string; border: string }

interface AuditLogTableProps {
  items: AuditLogRow[];
  colors: Colors;
}

/**
 * Simple HTML table listing recent deploy/rollback/delete events. Admin-
 * only. No pagination yet — server returns the most recent 200.
 */
export function AuditLogTable(props: AuditLogTableProps): React.JSX.Element {
  return (
    <div className="vx-table-wrap" style={{ overflowX: 'auto', border: `1px solid ${props.colors.border}`, borderRadius: '10px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: props.colors.card }}>
            <Th colors={props.colors}>Time</Th>
            <Th colors={props.colors}>App</Th>
            <Th colors={props.colors}>Event</Th>
            <Th colors={props.colors}>Version</Th>
            <Th colors={props.colors}>Actor</Th>
          </tr>
        </thead>
        <tbody>
          {props.items.length === 0 && (
            <tr>
              <td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: props.colors.textSecondary, fontFamily: FONTS.body, fontSize: '12px' }}>
                No audit events yet.
              </td>
            </tr>
          )}
          {props.items.map(row => (
            <Row key={row.id} row={row} colors={props.colors} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Table header cell. */
function Th(props: { colors: Colors; children: React.ReactNode }): React.JSX.Element {
  return (
    <th style={{ textAlign: 'left', padding: '10px 12px', fontFamily: FONTS.heading, fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase', color: props.colors.textSecondary }}>
      {props.children}
    </th>
  );
}

/** One audit-log row. */
function Row(props: { row: AuditLogRow; colors: Colors }): React.JSX.Element {
  const version = props.row.to_version
    ? `${props.row.from_version ?? '—'} → ${props.row.to_version}`
    : props.row.from_version
      ? String(props.row.from_version)
      : '—';
  return (
    <tr style={{ borderTop: `1px solid ${props.colors.border}` }}>
      <Td colors={props.colors}>{new Date(props.row.created_at).toLocaleString()}</Td>
      <Td colors={props.colors}>{props.row.app_slug ?? props.row.app_id}</Td>
      <Td colors={props.colors}>{props.row.event_type}</Td>
      <Td colors={props.colors}>{version}</Td>
      <Td colors={props.colors}>{props.row.actor_email ?? props.row.actor_sub}</Td>
    </tr>
  );
}

/** Table data cell. */
function Td(props: { colors: Colors; children: React.ReactNode }): React.JSX.Element {
  return (
    <td style={{ padding: '10px 12px', fontFamily: FONTS.body, fontSize: '12px', color: props.colors.text }}>
      {props.children}
    </td>
  );
}
